/** Theme-only Searchanise adapter. Uses the app's confirmed collection label,
 * never guesses a collection from a URL slug. Plain text prevents query markup
 * injection. Observer handles app rerenders without replacing controls, history, correction messages or purchase handlers.
 * Native collections remain the SEO destinations; app views retain noindex. */
(() => {
  'use strict';
  const intro = document.querySelector('[data-etr-results-intro]');
  if (!intro) return;
  const nativeCollection = intro.hasAttribute('data-etr-native-collection');
  const vendor = intro.dataset.etrVendor || (document.querySelector('[data-etr-pilot-template="sodii"]') && new URLSearchParams(location.search).get('q') === 'Sodii' && new URLSearchParams(location.search).get('rb_vendor') === 'Sodii' ? 'Sodii' : null);
  // Shopify's vendor query is a page identity, not a removable search facet.
  // Establish an exact vendor restriction before the app reads its URL state.
  const vendorValue = vendor?.replace(/[|,]/g, '\\$&');
  if (vendor) {
    intro.dataset.etrVendor = vendor;
    const url = new URL(location.href);
    // Match the native Sodii page's newest-first default on direct app visits.
    // An explicit shopper sort always wins; replacement avoids an extra Back step.
    const needsDefaultSort = !nativeCollection && vendor === 'Sodii' && !url.searchParams.has('sort_by');
    if (url.searchParams.get('q') !== vendor || url.searchParams.get('rb_vendor') !== vendorValue || needsDefaultSort) {
      url.searchParams.set('q', vendor);
      url.searchParams.set('rb_vendor', vendorValue);
      if (needsDefaultSort) url.searchParams.set('sort_by', 'created');
      location.replace(url.href);
      return;
    }
  }
  const setText = (node, value) => { if (node && node.textContent !== value) node.textContent = value; };
  const canonical = document.querySelector('link[rel="canonical"]');
  const originalCanonical = canonical?.href;
  const products = new Map();
  let vendorResultsReady = false;
  const money = new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' });
  let scheduled = false;
  function syncCard(card) {
    const button = card.querySelector('.snize-action-button');
    if (!button) return;
    const product = products.get(card.dataset.originalProductId);
    const variants = product?.shopify_variants;
    let choice = /view product|choose|quick shop/i.test(button.textContent);
    let soldOut = Boolean(card.querySelector('.snize-out-of-stock'));
    if (button.hasAttribute('data-etr-card-restock') && Array.isArray(variants) && variants.some(v => v.available === true || String(v.available) === '1')) {
      window.ETCardRestock?.restore(button);
      button.removeAttribute('data-etr-card-restock');
    }
    if (Array.isArray(variants) && variants.length) {
      const available = variants.filter(v => v.available === true || String(v.available) === '1');
      soldOut = variants.every(v => v.available === false || String(v.available) === '0');
      const names = Object.keys(variants[0].options || {});
      const name = names.length === 1 ? names[0].toLowerCase().replace('color', 'colour').replace('flavor', 'flavour') : '';
      const precise = variants.length > 1 && ['size', 'colour', 'flavour', 'scent'].includes(name);
      const nonDefault = names.length !== 1 || names[0] !== 'Title' || variants[0].options.Title !== 'Default Title';
      const requiresPlan = product.requires_selling_plan === '1' || product.requires_selling_plan === true;
      const plans = requiresPlan || Boolean(product.selling_plan_id && product.selling_plan_id !== 'undefined');
      choice = !available.length || variants.length > 1 || nonDefault || plans || choice;
      if (choice && !button.hasAttribute('data-etr-restock-ready')) setText(button, !available.length ? 'View product' : requiresPlan ? 'Explore subscription' : precise ? `Choose ${name}` : variants.length > 1 || plans ? 'Choose options' : 'View product');
      let options = card.querySelector('.etr-result-options');
      if (variants.length > 1) {
        if (!options) { options = document.createElement('span'); options.className = 'etr-result-options'; card.querySelector('.snize-price-list')?.after(options); }
        const count = precise ? new Set(variants.map(v => v.options[names[0]])).size : variants.length;
        setText(options, `${count} ${precise ? name + ' ' : ''}options`);
      } else if (options) options.remove();
      const prices = (available.length ? available : variants).map(v => Number(v.price)).filter(Number.isFinite);
      const price = Math.min(...prices);
      if (Number.isFinite(price)) {
        setText(card.querySelector('.snize-price'), `${new Set(variants.map(v => v.price)).size > 1 ? 'From ' : ''}${money.format(price)}`);
        const uniform = available.length && available.every(v => Number(v.price) === price && v.list_price === available[0].list_price);
        const compare = Number(available[0]?.list_price);
        const oldPrice = card.querySelector('.snize-discounted-price');
        if (oldPrice) oldPrice.hidden = !uniform || !(compare > price);
        const badge = card.querySelector('.snize-product-discount-label');
        if (badge) {
          const offer = available.some(v => Number(v.list_price) > Number(v.price));
          badge.hidden = !offer;
          if (offer) setText(badge, uniform ? `Save ${Math.round((compare-price)*100/compare)}%` : 'Selected options on sale');
        }
      }
    }
    const link = card.querySelector('a.snize-view-link');
    if (soldOut && link && card.dataset.originalProductId) {
      button.dataset.etrCardRestock = card.dataset.originalProductId;
      button.dataset.etrRestockUrl = link.href;
      button.dataset.etrRestockTitle = card.querySelector('.snize-title')?.textContent || '';
      window.ETCardRestock?.enhance(button);
      choice = true;
    } else if (button.hasAttribute('data-etr-card-restock')) {
      window.ETCardRestock?.restore(button);
      button.removeAttribute('data-etr-card-restock');
    }
    button.toggleAttribute('data-etr-choice', choice);
  }
  // Use a native selector instead of the app's conflicting hover/click menu.
  // The original options still own sorting, URL state and analytics.
  function syncSort(root) {
    const dropdown = root?.querySelector('.snize-main-panel-dropdown');
    const choices = dropdown?.querySelectorAll('a[role="option"]');
    if (!choices?.length) return;
    let select = dropdown.querySelector('[data-etr-sort]');
    if (!select) {
      select = document.createElement('select');
      select.dataset.etrSort = '';
      select.setAttribute('aria-label', 'Sort products');
      select.addEventListener('click', event => event.stopPropagation());
      select.addEventListener('change', event => {
        event.stopImmediatePropagation();
        const choice = [...dropdown.querySelectorAll('a[role="option"]')].find(a => a.textContent.trim() === select.value);
        choice?.click();
      });
      dropdown.append(select);
      dropdown.dataset.etrNativeSort = '';
    }
    const labels = [...choices].map(a => a.textContent.trim());
    if (JSON.stringify([...select.options].map(o => o.value)) !== JSON.stringify(labels)) {
      select.replaceChildren(...labels.map(label => new Option(label, label)));
    }
    const selected = [...choices].find(a => a.getAttribute('aria-selected') === 'true' || a.classList.contains('current'));
    if (selected) select.value = selected.textContent.trim();
  }
  const originalIntro = [...intro.childNodes].map(node => node.cloneNode(true));
  const originalMeta = document.querySelector('meta[name="description"]')?.content;
  // Load only the confirmed collection's guide, once per page session.
  // A missing guide leaves the existing app introduction intact.
  const requestedGuides = new Set();
  function requestGuide(handle) {
    if (!handle || !/^[a-z0-9][a-z0-9-]*$/.test(handle) || requestedGuides.has(handle)) return;
    requestedGuides.add(handle);
    const base = window.Shopify?.routes?.root || '/';
    const url = new URL(`${base}collections/${handle}`, location.origin);
    url.searchParams.set('section_id', 'etr-collection-content');
    const preview = new URLSearchParams(location.search).get('preview_theme_id');
    if (preview && /^\d+$/.test(preview)) url.searchParams.set('preview_theme_id', preview);
    fetch(url, { credentials: 'same-origin' }).then(response => {
      if (!response.ok) throw new Error('Collection guide unavailable');
      return response.text();
    }).then(markup => {
      const parsed = new DOMParser().parseFromString(markup, 'text/html');
      const source = parsed.querySelector('.etr-pilot-intro');
      const guide = parsed.querySelector('.etr-pilot-guide');
      if (source?.dataset.pilotKey !== handle || !guide) return;
      const template = document.createElement('template');
      template.dataset.etrPilotTemplate = handle;
      template.content.append(source, guide);
      document.getElementById('MainContent').append(template);
      // update reads the current app identity, protecting against late responses.
      update();
    }).catch(() => {});
  }
  function syncPilot(handle) {
    const template = [...document.querySelectorAll('[data-etr-pilot-template]')]
      .find(node => node.dataset.etrPilotTemplate === handle);
    if (nativeCollection) return Boolean(document.querySelector('[data-etr-pilot]'));
    if (!template) {
      requestGuide(handle);
      if (intro.dataset.etrPilot) {
        intro.replaceChildren(...originalIntro.map(node => node.cloneNode(true)));
        delete intro.dataset.etrPilot;
        document.querySelector('[data-etr-app-guide]')?.remove();
        const meta = document.querySelector('meta[name="description"]');
        if (meta && originalMeta !== undefined) meta.content = originalMeta;
      }
      return false;
    }
    if (intro.dataset.etrPilot !== handle) {
      const source = template.content.querySelector('.etr-pilot-intro');
      intro.replaceChildren(source.cloneNode(true));
      intro.dataset.etrPilot = handle;
      document.querySelector('[data-etr-app-guide]')?.remove();
      const guide = template.content.querySelector('.etr-pilot-guide').cloneNode(true);
      guide.dataset.etrAppGuide = handle;
      document.getElementById('snize_results')?.after(guide);
    }
    const source = intro.querySelector('[data-pilot-title]');
    if (document.title !== source.dataset.pilotTitle) document.title = source.dataset.pilotTitle;
    const meta = document.querySelector('meta[name="description"]');
    if (meta && meta.content !== source.dataset.pilotMeta) meta.content = source.dataset.pilotMeta;
    return true;
  }
  function syncPilotFilters(key) {
    if (!['health-beauty', 'gut-health', 'laundry', 'pasta-grains-cereal', 'sodii'].includes(key)) {
      document.querySelectorAll('[data-etr-pilot-filter-hidden]').forEach(block => block.removeAttribute('data-etr-pilot-filter-hidden'));
      return;
    }
    document.querySelectorAll('.snize-product-filters-block, .snize-product-filter-row').forEach(block => {
      const opener = block.querySelector('.snize-product-filter-opener');
      const attribute = opener?.dataset.seFacetAttribute || block.id.replace('snize_filters_block_', '');
      const label = block.querySelector('.snize-product-filter-title, .snize-product-filters-title > span')?.firstChild?.textContent.trim().toLowerCase();
      const base = ['price', 'stock_status', 'product_type', ...(key === 'sodii' ? [] : ['vendor'])];
      const optional = ['size', 'flavour', 'flavor'].includes(label);
      block.toggleAttribute('data-etr-pilot-filter-hidden', !base.includes(attribute) && !optional);
    });
  }
  const petCategories = [
    ['pet-supplies', 'All Pet Supplies', 'Pet Supplies'],
    ['pet-food-nutrition', 'Food & Treats', 'Pet Food & Nutrition'],
    ['pet-supplements', 'Health & Supplements', 'Pet Health & Supplements'],
    ['natural-pet-shampoo-wash', 'Grooming', 'Natural Pet Shampoo & Grooming'],
    ['pet-dental-care', 'Dental Care', 'Pet Dental Care'],
    ['eco-cat-litter', 'Cat Litter', 'Eco Cat Litter'],
    ['pet-accessories', 'Accessories', 'Pet Accessories']
  ];
  const petKeys = new Set(petCategories.map(([handle]) => handle));
  const petCategoryTitles = new Map(petCategories.map(([handle, , title]) => [handle, title]));
  const petFilterState = new WeakMap();
  const petChipState = new WeakMap();
  const petEmptyState = new WeakMap();
  let nativePetEmptySearch = null;
  const petSizeKey = 'filter_option_6f6cb72d544962fa333e2e34ce64f719';
  const petSharedParams = new Set(['rb_snize_facet1', 'rb_vendor', 'rb_price', 'rb_stock_status', 'preview_theme_id']);
  function petCategoryUrl(handle) {
    const base = window.Shopify?.routes?.root || '/';
    const url = new URL(base + 'collections/' + handle, location.origin);
    new URLSearchParams(location.search).forEach((value, key) => {
      if (petSharedParams.has(key)) url.searchParams.append(key, value);
    });
    return url.href;
  }
  function placePetNodes(container, nodes) {
    let next = container.firstElementChild;
    nodes.forEach(node => {
      if (node !== next) container.insertBefore(node, next);
      next = node.nextElementSibling;
    });
  }
  function syncPetFilterChips(root, key) {
    const active = petKeys.has(key);
    root.toggleAttribute('data-etr-pet-selected-overview', active);
    let state = petChipState.get(root);
    if (!active) {
      state?.node.remove();
      return;
    }
    if (!state) {
      const node = document.createElement('div');
      node.className = 'etr-pet-selected-filters';
      node.setAttribute('role', 'group');
      node.setAttribute('aria-label', 'Applied filters');
      node.addEventListener('click', event => {
        const button = event.target.closest('button[data-etr-pet-remove-filter]');
        if (!button || !node.contains(button)) return;
        const url = new URL(location.href);
        url.searchParams.delete(button.dataset.etrPetRemoveFilter);
        url.searchParams.delete('page');
        // A deliberate navigation removes only this facet; category, sort,
        // preview and all other applied filters remain in the current URL.
        location.assign(url.href);
      });
      state = { node, signature: null };
      petChipState.set(root, state);
    }
    const panel = root.querySelector('.snize-main-panel');
    if (panel) {
      if (panel.nextElementSibling !== state.node) panel.after(state.node);
    } else if (root.firstElementChild !== state.node) root.prepend(state.node);
    const labels = new Map([
      ['snize_facet1', 'Shop by pet'], ['vendor', 'Brand'], ['price', 'Price'],
      ['stock_status', 'Availability'], ['discount_relative', 'On sale'],
      ['product_type', 'Product type'], ['snize_facet2', 'Type'],
      ['snize_facet3', 'Flavour'], ['snize_facet4', 'Material'], ['snize_facet5', 'Scent'],
      ['snize_facet6', 'Features'], ['snize_facet7', 'Protein'], ['snize_facet9', 'Life stage'],
      ['snize_facet10', 'Concern'], ['snize_facet11', 'Food type'],
      [petSizeKey, key === 'pet-food-nutrition' ? 'Size / pack weight' : key === 'eco-cat-litter' ? 'Bag weight' : 'Size']
    ]);
    const facets = new Map();
    new URLSearchParams(location.search).forEach((value, name) => {
      if (!name.startsWith('rb_') || !value) return;
      if (!facets.has(name)) facets.set(name, []);
      facets.get(name).push(value);
    });
    const entries = [...facets].map(([name, values]) => {
      const attribute = name.slice(3);
      let label = labels.get(attribute);
      if (!label) {
        const opener = [...root.querySelectorAll('[data-se-facet-attribute]')].find(node => node.dataset.seFacetAttribute === attribute && node.matches('.snize-product-filter-opener'));
        const block = document.getElementById('snize_filters_block_' + attribute);
        label = (opener || block?.querySelector('.snize-product-filters-title > span, .snize-product-filter-title'))?.textContent.trim();
      }
      let value = values.join('|').split(/(?<!\\)\|/).map(item => item.replace(/\\([|,])/g, '$1')).join(', ');
      if (attribute === 'price') {
        const range = values[0].split(',');
        if (range.length === 2 && range.every(item => item.trim() && Number.isFinite(Number(item)))) value = range.map(item => money.format(Number(item))).join(' – ');
      }
      const display = attribute === 'discount_relative' ? 'On sale' : label ? label + ': ' + value : 'Other filter applied';
      return { name, display };
    });
    const signature = JSON.stringify(entries);
    state.node.hidden = entries.length === 0;
    if (state.signature === signature) return;
    state.signature = signature;
    const buttons = entries.map(({ name, display }) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.dataset.etrPetRemoveFilter = name;
      button.setAttribute('aria-label', 'Remove filter: ' + display);
      const label = document.createElement('span');
      label.textContent = display;
      const close = document.createElement('span');
      close.textContent = '×';
      close.setAttribute('aria-hidden', 'true');
      button.append(label, close);
      return button;
    });
    state.node.replaceChildren(...buttons);
  }
  function syncPetEmptyMessage(root, empty) {
    let state = petEmptyState.get(root);
    if (!empty) {
      state?.node.remove();
      return;
    }
    if (!state) {
      const node = document.createElement('div');
      node.className = 'etr-pet-empty-results';
      const message = document.createElement('p');
      message.setAttribute('role', 'status');
      message.textContent = 'No products match these filters. Remove an applied filter or clear all filters to explore this category.';
      const clear = document.createElement('a');
      clear.textContent = 'Clear all filters';
      node.append(message, clear);
      state = { node, clear };
      petEmptyState.set(root, state);
    }
    const url = new URL(location.href);
    [...url.searchParams.keys()].filter(name => name.startsWith('rb_') || name === 'page').forEach(name => url.searchParams.delete(name));
    if (state.clear.href !== url.href) state.clear.href = url.href;
    const chips = root.querySelector('.etr-pet-selected-filters');
    if (chips) {
      if (chips.nextElementSibling !== state.node) chips.after(state.node);
    } else if (root.firstElementChild !== state.node) root.prepend(state.node);
  }
  function syncPetFilters(root, key) {
    if (!root) return;
    const active = petKeys.has(key);
    syncPetFilterChips(root, key);
    const hosts = [root.querySelector('.snize-filters-sidebar'), document.getElementById('snize-modal-mobile-filters-dialog')].filter(Boolean);
    hosts.forEach(host => {
      const container = host.matches('.snize-modal') ? host.querySelector('#snize_filters_block') : host;
      if (!container) return;
      let state = petFilterState.get(container);
      if (!active) {
        if (!state?.active) return;
        state.active = false;
        host.removeAttribute('data-etr-pet-filters');
        container.querySelectorAll('[data-etr-pet-filter-hidden]').forEach(node => node.removeAttribute('data-etr-pet-filter-hidden'));
        state.titles.forEach((value, node) => { if (node.isConnected && node.nodeValue !== value) node.nodeValue = value; });
        const stock = state.availability.querySelector('[data-etr-filter-switch="stock_status"]');
        if (stock && state.panel.isConnected) state.panel.append(stock);
        state.category.remove();
        state.availability.remove();
        placePetNodes(container, state.order.filter(node => node.parentElement === container));
        return;
      }
      const panel = container.querySelector('.etr-filter-quick');
      if (!panel) return;
      if (!state || !state.category.isConnected) {
        const category = document.createElement('details');
        category.className = 'etr-pet-category';
        const summary = document.createElement('summary');
        summary.textContent = 'Product category';
        const nav = document.createElement('nav');
        nav.setAttribute('aria-label', 'Pet product categories');
        petCategories.forEach(([handle, title]) => {
          const link = document.createElement('a');
          link.dataset.etrPetCategory = handle;
          link.textContent = title;
          nav.append(link);
        });
        category.append(summary, nav);
        const availability = document.createElement('details');
        availability.className = 'etr-pet-availability';
        const availabilityTitle = document.createElement('summary');
        availabilityTitle.textContent = 'Availability';
        availability.append(availabilityTitle);
        state = { active: true, panel, category, availability, order: [...container.children], titles: new Map() };
        petFilterState.set(container, state);
      }
      state.active = true;
      state.panel = panel;
      if (host.dataset.etrPetFilters !== key) host.dataset.etrPetFilters = key;
      state.category.querySelectorAll('[data-etr-pet-category]').forEach(link => {
        const handle = link.dataset.etrPetCategory;
        const href = petCategoryUrl(handle);
        if (link.href !== href) link.href = href;
        if (handle === key) {
          if (link.getAttribute('aria-current') !== 'page') link.setAttribute('aria-current', 'page');
        } else link.removeAttribute('aria-current');
      });
      const stock = host.querySelector('[data-etr-filter-switch="stock_status"]');
      if (stock && stock.parentElement !== state.availability) state.availability.append(stock);
      const groups = new Map();
      const labels = new Map([['snize_facet1', 'Shop by pet'], ['vendor', 'Brand'], ['price', 'Price']]);
      if (key === 'pet-food-nutrition') {
        labels.set('snize_facet7', 'Protein');
        labels.set('snize_facet3', 'Flavour');
      }
      if (key === 'eco-cat-litter') labels.set('snize_facet5', 'Scent');
      container.querySelectorAll('.snize-product-filters-block, .snize-product-filter-row').forEach(block => {
        const opener = block.querySelector('.snize-product-filter-opener');
        const name = opener?.dataset.seFacetAttribute || block.id.replace('snize_filters_block_', '');
        if (name === petSizeKey && ['pet-food-nutrition', 'eco-cat-litter'].includes(key)) {
          const values = [...block.querySelectorAll('input')].map(input => input.value.trim()).filter(Boolean);
          if (values.length && key === 'pet-food-nutrition') labels.set(name, 'Size / pack weight');
          if (values.length && key === 'eco-cat-litter' && values.every(value => /^\d+(?:\.\d+)?\s*kg$/i.test(value))) labels.set(name, 'Bag weight');
        }
        block.toggleAttribute('data-etr-pet-filter-hidden', !labels.has(name));
        if (!labels.has(name)) return;
        groups.set(name, block);
        const heading = opener?.querySelector('.snize-product-filter-title') || opener || block.querySelector('.snize-product-filters-title > span, .snize-product-filter-title');
        const titleNode = [...(heading?.childNodes || [])].find(node => node.nodeType === Node.TEXT_NODE && node.nodeValue.trim());
        if (titleNode) {
          if (!state.titles.has(titleNode)) state.titles.set(titleNode, titleNode.nodeValue);
          if (titleNode.nodeValue !== labels.get(name)) titleNode.nodeValue = labels.get(name);
        }
      });
      const ordered = [panel, groups.get('snize_facet1'), state.category, groups.get('vendor'), groups.get('price'), state.availability,
        ...['snize_facet7', 'snize_facet3', 'snize_facet5', petSizeKey].map(name => groups.get(name))].filter(Boolean);
      // Keep the real inputs and their app-owned handlers. Move only when needed:
      // the shared observer must reach a stable DOM after each app update.
      placePetNodes(container, ordered);
    });
  }
  const expandedFilters = new Set();
  const quickFilterNames = ['discount_relative', 'stock_status'];
  const saleValue = value => /^\d+(?:\.\d+)?%$/.test(value) && parseFloat(value) > 0;
  let filterDialog;
  let filterDialogObserver;
  function syncFilterDrawerTeaser() {
    const dialog = document.getElementById('snize-modal-mobile-filters-dialog');
    if (dialog !== filterDialog) {
      filterDialogObserver?.disconnect();
      filterDialog = dialog;
      if (dialog) {
        filterDialogObserver = new MutationObserver(syncFilterDrawerTeaser);
        filterDialogObserver.observe(dialog, { attributes: true, attributeFilter: ['class', 'style', 'hidden', 'aria-hidden'] });
      }
    }
    const form = document.querySelector('shopify-forms-embed#app-embed-container-1186415');
    const open = Boolean(dialog && dialog.getClientRects().length && !dialog.hidden && dialog.getAttribute('aria-hidden') !== 'true' &&
      getComputedStyle(dialog).display !== 'none' && getComputedStyle(dialog).visibility !== 'hidden');
    // Hide only the app's teaser presentation; signup fields and consent stay app-owned.
    form?.toggleAttribute('data-etr-filter-drawer-open', open);
  }
  function syncQuickFilters(root) {
    syncFilterDrawerTeaser();
    if (!root) return;
    const count = root.querySelector('.snize-main-panel-collection-results-found, .snize-main-panel-results-found')?.textContent.match(/([\d,]+)\s+(?:results|products)/i)?.[1];
    const hosts = [root.querySelector('.snize-filters-sidebar'), document.getElementById('snize-modal-mobile-filters-dialog')].filter(Boolean);
    hosts.forEach(host => {
      const mobile = host.matches('.snize-modal');
      const container = mobile ? host.querySelector('#snize_filters_block') : host;
      if (!container) return;
      host.setAttribute('data-etr-filter-enhanced', '');
      let panel = container.querySelector('.etr-filter-quick');
      if (!panel) {
        panel = document.createElement('div');
        panel.className = 'etr-filter-quick';
        panel.innerHTML = '<div class="etr-filter-heading"><h2>Filter</h2><button type="button" data-etr-filter-clear>Clear all</button></div><p class="etr-filter-count" role="status" aria-live="polite"></p><button type="button" class="etr-filter-switch" role="switch" aria-checked="false" data-etr-filter-switch="discount_relative"><span>On sale</span><span class="etr-switch-track" aria-hidden="true"></span></button><button type="button" class="etr-filter-switch" role="switch" aria-checked="false" data-etr-filter-switch="stock_status"><span>In stock</span><span class="etr-switch-track" aria-hidden="true"></span></button>';
        container.prepend(panel);
      }
      setText(panel.querySelector('.etr-filter-count'), count ? `${count} ${count === '1' ? 'product' : 'products'}` : 'Refine your results');
      const params = new URLSearchParams(location.search);
      panel.querySelector('[data-etr-filter-clear]').disabled = ![...params].some(([key, value]) => key.startsWith('rb_') && value && !(vendor && key === 'rb_vendor'));
      quickFilterNames.forEach(name => {
        const inputs = [...host.querySelectorAll(`input[name="${name}"]`)];
        const eligible = inputs.filter(input => name === 'stock_status' ? input.value === 'In Stock' : saleValue(input.value));
        const selected = inputs.filter(input => input.checked);
        const checked = eligible.length > 0 && selected.length === eligible.length && eligible.every(input => input.checked);
        const button = host.querySelector(`[data-etr-filter-switch="${name}"]`);
        button.setAttribute('aria-checked', String(checked));
        button.disabled = !eligible.length && !checked;
      });
      if (mobile) {
        // Current Searchanise markup uses flat openers with sibling option panels.
        // Normalise once so the existing disclosure layout keeps each real panel in flow.
        [...container.children].filter(node => node.matches('.snize-product-filter-opener')).forEach(opener => {
          const name = opener.dataset.seFacetAttribute;
          const group = name && document.getElementById(opener.getAttribute('aria-controls') || `snize_product_filter_${name}`);
          if (!group || !host.contains(group)) return;
          const row = document.createElement('div');
          row.className = 'snize-product-filter-row';
          row.dataset.etrFilterRow = name;
          opener.before(row);
          row.append(opener);
          opener.setAttribute('aria-controls', group.id);
        });
        // Keep the app's real inputs and handlers, but expand them in this drawer.
        // No duplicate filters or second product requests are introduced.
        container.querySelectorAll('.snize-product-filter-row').forEach(row => {
          const opener = row.querySelector('.snize-product-filter-opener');
          const name = opener?.dataset.seFacetAttribute;
          const group = name && host.querySelector(`#${opener.getAttribute('aria-controls')}`);
          if (!group || quickFilterNames.includes(name)) return;
          if (group.parentElement !== row) row.append(group);
          row.dataset.etrDisclosure = name;
          const open = expandedFilters.has(name);
          row.toggleAttribute('data-etr-expanded', open);
          opener.setAttribute('role', 'button');
          opener.tabIndex = 0;
          opener.setAttribute('aria-expanded', String(open));
        });
      }
    });
    const saleActive = hosts.some(host => host.querySelector('[data-etr-filter-switch="discount_relative"]')?.getAttribute('aria-checked') === 'true');
    document.querySelectorAll('.snize-selected-filters').forEach(list => {
      const chips = [...list.querySelectorAll('.snize-filter-button')].filter(chip => chip.querySelector('[data-se-facet-attribute="discount_relative"]'));
      chips.forEach((chip, index) => {
        chip.toggleAttribute('data-etr-sale-chip', saleActive && index === 0);
        chip.toggleAttribute('data-etr-sale-chip-hidden', saleActive && index > 0);
        const clear = chip.querySelector('[data-se-facet-attribute="discount_relative"]');
        const label = saleActive ? 'On sale' : clear.dataset.seFacetValue;
        setText(chip.querySelector('.snize-clear-the-filter'), label);
        clear.setAttribute('aria-label', `Remove filter: ${label}`);
      });
    });
    document.querySelectorAll('.snize-selected-filters-container').forEach(list => {
      [...list.querySelectorAll('[data-se-facet-attribute="discount_relative"][role="button"]')].forEach((chip, index) => {
        chip.toggleAttribute('data-etr-sale-chip', saleActive && index === 0);
        chip.toggleAttribute('data-etr-sale-chip-hidden', saleActive && index > 0);
        const label = saleActive ? 'On sale' : chip.dataset.seFacetValue;
        if (chip.firstChild?.nodeType === Node.TEXT_NODE && chip.firstChild.textContent !== label) chip.firstChild.textContent = label;
        chip.setAttribute('aria-label', `Remove filter: ${label}`);
      });
    });
    const params = new URLSearchParams(location.search);
    const filterCount = [...params].filter(([key, value]) => key.startsWith('rb_') && value && !(vendor && key === 'rb_vendor'))
      .reduce((total, [key, value]) => total + (saleActive && key === 'rb_discount_relative' ? 1 : value.split(/(?<!\\)\|/).length), 0);
    document.querySelectorAll('.snize-selected-filters-count-container').forEach(node => { setText(node, String(filterCount)); node.hidden = filterCount === 0; });
    document.querySelectorAll('.snize-product-filters-show-products').forEach(node => { if (node.textContent.trim() === 'Show 1 products') setText(node, 'Show 1 product'); });
  }
  function filterInteraction(event) {
    const target = event.target.closest('[data-etr-filter-switch], [data-etr-filter-clear], [data-etr-sale-chip][role="button"], [data-etr-sale-chip] [role="button"], [data-etr-disclosure] > .snize-product-filter-opener, #snize-modal-mobile-filters-dialog[data-etr-filter-enhanced] .snize-product-filters-show-products');
    if (!target || event.type === 'keydown' && !['Enter', ' '].includes(event.key)) return;
    if (target.matches('button') && event.type === 'keydown') return; // Native button activation.
    event.preventDefault();
    event.stopImmediatePropagation();
    if (target.disabled) return;
    if (target.closest('[data-etr-sale-chip]')) {
      document.querySelector('[data-etr-filter-switch="discount_relative"][aria-checked="true"]')?.click();
      return;
    }
    const host = target.closest('[data-etr-filter-enhanced]');
    if (target.matches('.snize-product-filters-show-products')) {
      // Filters apply immediately; use the app's working close control to return to results.
      host.querySelector('.snize-close-button')?.click();
      return;
    }
    if (target.matches('[data-etr-filter-clear]')) {
      const reset = document.querySelector('.snize-product-filters-reset-all');
      if (reset) reset.click();
      else {
        const url = new URL(location.href);
        [...url.searchParams.keys()].filter(key => key === 'page' || key.startsWith('rb_') && !(vendor && key === 'rb_vendor')).forEach(key => url.searchParams.delete(key));
        location.assign(url.href);
      }
      return;
    }
    if (target.hasAttribute('data-etr-filter-switch')) {
      const name = target.dataset.etrFilterSwitch;
      const on = target.getAttribute('aria-checked') !== 'true';
      const inputs = [...host.querySelectorAll(`input[name="${name}"]`)];
      const eligible = input => name === 'stock_status' ? input.value === 'In Stock' : saleValue(input.value);
      const trigger = inputs.find(eligible);
      if (!trigger) return;
      // Select every indexed discount band (not just products on this page),
      // then let one native click submit the entire current filter selection.
      inputs.forEach(input => { input.checked = on && eligible(input); });
      trigger.checked = !on;
      trigger.click();
      target.setAttribute('aria-checked', String(on));
      return;
    }
    const row = target.closest('[data-etr-disclosure]');
    const name = row.dataset.etrDisclosure;
    if (expandedFilters.has(name)) expandedFilters.delete(name); else expandedFilters.add(name);
    row.toggleAttribute('data-etr-expanded', expandedFilters.has(name));
    target.setAttribute('aria-expanded', String(expandedFilters.has(name)));
  }
  document.addEventListener('click', filterInteraction, true);
  document.addEventListener('keydown', filterInteraction, true);
  function update() {
    scheduled = false;
    const root = document.getElementById('snize_results');
    syncSort(root);
    const label = root?.querySelector('.snize-main-panel-collection-results-found')?.textContent.match(/"([^"\n]+)"/);
    const params = new URLSearchParams(location.search);
    const query = params.get('q');
    const handle = params.get('collection');
    const category = !query && /^[a-z0-9][a-z0-9-]*$/.test(handle || '') && label ? label[1] : null;
    const nativePetKey = intro.dataset.etrNativeCollectionHandle;
    const hasActivePetFilters = [...params].some(([name, value]) => name.startsWith('rb_') && value);
    // Cached/initial results may render before the response listener connects.
    // Require the app's explicit empty state and zero total, never just no cards.
    const renderedPetEmpty = Boolean(root?.classList.contains('snize-nothing-found') &&
      root.querySelector('.snize-products-tab-total')?.textContent.trim() === '0' && !root.querySelector('.snize-product'));
    const nativePetEmpty = nativeCollection && !vendor && !query && hasActivePetFilters &&
      petCategoryTitles.get(nativePetKey) === intro.dataset.etrNativeCollection && (!label || label[1] === intro.dataset.etrNativeCollection) &&
      (nativePetEmptySearch !== null && nativePetEmptySearch === location.search || renderedPetEmpty);
    const nativePanel = document.querySelector('[data-etr-native-results]');
    if (nativeCollection && nativePanel) {
      // Vendor mode has a result count but no quoted collection label. Reveal
      // it only after the app confirms the exact selected vendor restriction.
      const selectedVendors = [...(root?.querySelectorAll('input[name="vendor"]:checked') || [])];
      const matchesVendor = vendor && params.get('rb_vendor') === vendorValue &&
        (vendorResultsReady || selectedVendors.length === 1 && selectedVendors[0].value === vendor) &&
        [...root.querySelectorAll('.snize-product .snize-attribute')].every(node => node.textContent.trim() === vendor);
      const matchesCollection = vendor ? matchesVendor : Boolean(label && label[1] === intro.dataset.etrNativeCollection || nativePetEmpty);
      nativePanel.hidden = !matchesCollection;
      const fallback = document.querySelector('[data-etr-native-fallback]');
      if (fallback) fallback.hidden = Boolean(matchesCollection);
    }
    if (vendor) {
      // Keep the drawer focused. Brand is already chosen; discount and unrelated
      // category attributes distract. Optional size/flavour appear when indexed.
      document.querySelectorAll('.snize-product-filters-block, .snize-product-filter-row').forEach(block => {
        const opener = block.querySelector('.snize-product-filter-opener');
        const key = opener?.dataset.seFacetAttribute || block.id.replace('snize_filters_block_', '');
        const title = block.querySelector('.snize-product-filter-title, .snize-product-filters-title > span')?.firstChild?.textContent.trim().toLowerCase();
        const useful = ['price', 'stock_status', 'product_type'].includes(key) || ['size', 'flavour', 'flavor'].includes(title);
        block.toggleAttribute('data-etr-vendor-filter-hidden', !useful);
      });
      const count = [...params].filter(([key, value]) => key.startsWith('rb_') && key !== 'rb_vendor' && value)
        .reduce((total, [, value]) => total + value.split(/(?<!\\)\|/).length, 0);
      document.querySelectorAll('.snize-selected-filters-count-container, .snize-product-filters-header .snize-product-filters-count').forEach(node => {
        setText(node, String(count));
        node.hidden = count === 0;
      });
      document.querySelectorAll('.snize-product-filters-reset-all').forEach(node => { node.hidden = count === 0; });
    }
    const pilotKey = nativeCollection ? document.querySelector('.etr-pilot')?.dataset.etrPilot : (category ? handle : vendor === 'Sodii' ? 'sodii' : null);
    const pilotActive = syncPilot(pilotKey);
    if (root && root.dataset.etrPilotResults !== (pilotActive ? pilotKey : '')) root.dataset.etrPilotResults = pilotActive ? pilotKey : '';
    syncPilotFilters(pilotActive ? pilotKey : null);
    syncQuickFilters(root);
    const candidatePetKey = nativeCollection ? intro.dataset.etrNativeCollectionHandle || pilotKey : handle;
    const confirmedPetTitle = nativeCollection ? intro.dataset.etrNativeCollection : petCategoryTitles.get(candidatePetKey);
    syncPetFilters(root, label && label[1] === confirmedPetTitle || nativePetEmpty ? candidatePetKey : null);
    if (root) syncPetEmptyMessage(root, nativePetEmpty);
    const title = category ? `${category} | Eco Traders` : query ? `Search results for “${query.slice(0, 100)}” | Eco Traders` : 'Shop everyday essentials | Eco Traders';
    if (!nativeCollection && !pilotActive && document.title !== title) document.title = title;
    // Do not rewrite location/history: Searchanise owns filter, sort and back state.
    // Only a category confirmed by the app can point to a native collection.
    const canonicalUrl = category ? `${location.origin}/collections/${handle}` : pilotActive && vendor === 'Sodii' ? `${location.origin}/collections/vendors?q=Sodii` : originalCanonical;
    if (!nativeCollection && canonical && canonicalUrl && canonical.href !== canonicalUrl) canonical.href = canonicalUrl;
    if (!nativeCollection && !pilotActive) setText(intro.querySelector('h1'), category ? category : query ? `Search results for “${query.slice(0, 100)}”` : 'Shop everyday essentials');
    if (!nativeCollection && !pilotActive) setText(intro.querySelector('p'), category ? 'Compare products, brands and sizes. Find what suits your everyday.' : 'Find the brands, formats and prices that suit you.');
    // Compare-at is not automatically RRP. Match the homepage's nearest whole
    // percent and neutral wording, using the prices currently shown by the app.
    root?.querySelectorAll('.snize-product').forEach(card => {
      const badge = card.querySelector('.snize-product-discount-label');
      syncCard(card);
      if (!badge || products.has(card.dataset.originalProductId)) return;
      const amount = selector => {
        const text = card.querySelector(selector)?.textContent || '';
        const match = text.replace(/,/g, '').match(/\d+(?:\.\d{1,2})?/);
        return match ? Math.round(Number(match[0]) * 100) : NaN;
      };
      const price = amount('.snize-price'), original = amount('.snize-discounted-price');
      if (original > price && price >= 0) setText(badge, `Save ${Math.round((original - price) * 100 / original)}%`);
    });
  }
  const observer = new MutationObserver(() => {
    if (!scheduled) { scheduled = true; requestAnimationFrame(update); }
  });
  observer.observe(document.body, { childList: true, subtree: true, characterData: true });
  // Searchanise can restore the address without restoring its rendered filters.
  // Reload that history entry so products, controls and metadata agree with the URL.
  window.addEventListener('popstate', () => location.reload());
  // Searchanise loads different drawer assets at initial page width. Reinitialise
  // only when crossing its mobile breakpoint, preserving the current URL state.
  const mobileLayout = window.matchMedia('(max-width: 767px)');
  let resizeReload;
  mobileLayout.addEventListener('change', () => {
    clearTimeout(resizeReload);
    resizeReload = setTimeout(() => location.reload(), 200);
  });
  // Reuse Searchanise's response: no additional per-product network requests.
  function connectSearchanise() {
    const $ = window.Searchanise?.$;
    if (!$ || connectSearchanise.connected) return;
    connectSearchanise.connected = true;
    $(document).on('Searchanise.DataRequestEnd', (event, type, params, data) => {
      if (type !== 'results') return;
      nativePetEmptySearch = null;
      const currentParams = new URLSearchParams(location.search);
      const hasPetFilters = [...currentParams].some(([name, value]) => name.startsWith('rb_') && value);
      const nativePetKey = intro.dataset.etrNativeCollectionHandle;
      // Native pet identity comes from Liquid, not a requested collection slug.
      // Only a successful empty response may suppress the unfiltered fallback.
      if (nativeCollection && !vendor && !currentParams.get('q') && hasPetFilters &&
          petCategoryTitles.get(nativePetKey) === intro.dataset.etrNativeCollection &&
          Array.isArray(data.items) && data.items.length === 0 && (data.totalItems === 0 || data.totalItems === '0')) {
        nativePetEmptySearch = location.search;
      }
      // A valid empty response must keep the filtered empty state visible,
      // rather than fall back to the unfiltered Shopify product grid.
      if (vendor) vendorResultsReady = params.restrictBy?.vendor === vendorValue &&
        Array.isArray(data.items) && data.items.every(item => item.vendor === vendor);
      products.clear();
      (data.items || []).forEach(item => products.set(String(item.original_product_id || item.product_id), item));
      update();
    });
    $(document).on('Searchanise.ResultsUpdated', update);
  }
  document.addEventListener('Searchanise.Loaded', connectSearchanise);
  connectSearchanise();
  if (vendor) {
    // Clearing shopper filters must retain the brand and preview/sort state.
    // Capture before the app's delegated reset handler, including its mobile drawer.
    const resetVendorFilters = event => {
      if (event.type === 'keydown' && event.key !== 'Enter' && event.key !== ' ') return;
      const reset = event.target.closest('.snize-product-filters-reset-all');
      if (!reset) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const url = new URL(location.href);
      [...url.searchParams.keys()].filter(key => key.startsWith('rb_') || key === 'page').forEach(key => url.searchParams.delete(key));
      url.searchParams.set('q', vendor);
      url.searchParams.set('rb_vendor', vendorValue);
      location.assign(url.href);
    };
    document.addEventListener('click', resetVendorFilters, true);
    document.addEventListener('keydown', resetVendorFilters, true);
  }
  document.getElementById('MainContent').addEventListener('click', event => {
    const button = event.target.closest('[data-etr-choice]');
    if (!button) return;
    const link = button.closest('.snize-product')?.querySelector('a.snize-view-link');
    if (!link) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    location.assign(link.href);
  }, true);
  update();
})();


